import { useEffect, useState } from 'react'
import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { Button } from '@/components/ui/button'
import { useInstallPrompt } from '@/shared/hooks/useInstallPrompt'

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
      <circle cx="5" cy="12" r="1.5" />
    </svg>
  )
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  )
}

function Banner({
  children,
  onDismiss,
}: {
  children: React.ReactNode
  onDismiss: () => void
}) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  function handleDismiss() {
    setVisible(false)
    setTimeout(onDismiss, 150)
  }

  return (
    <div
      className="border-border bg-background fixed right-3 bottom-[calc(56px+0.75rem)] left-3 z-50 rounded-md border p-3 shadow-lg transition-all duration-150 ease-out motion-reduce:transition-none"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      }}
      role="status"
    >
      <button
        onClick={handleDismiss}
        className="border-border bg-background text-muted-foreground hover:text-foreground absolute -top-1.5 -right-1.5 flex h-8 w-8 items-center justify-center rounded-full border shadow-sm before:absolute before:inset-[-8px] before:content-['']"
        aria-label={t`Close`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
      {children}
    </div>
  )
}

export function InstallPrompt() {
  const state = useInstallPrompt()
  const [expanded, setExpanded] = useState(false)

  if (state.type === 'hidden') return null

  if (state.type === 'native') {
    return (
      <Banner onDismiss={state.dismiss}>
        <div className="flex items-center gap-3">
          <img
            src="/icon-192x192.png"
            alt=""
            className="h-10 w-10 shrink-0 rounded"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm leading-tight font-semibold">
              <Trans>Install Fiduly Expenses</Trans>
            </p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              <Trans>Quick access from your home screen</Trans>
            </p>
          </div>
          <Button size="sm" onClick={state.install}>
            <Trans>Install</Trans>
          </Button>
        </div>
      </Banner>
    )
  }

  // iOS Safari — collapsed: one-liner with "How?" toggle; expanded: numbered steps
  return (
    <Banner onDismiss={state.dismiss}>
      <div className="flex items-center gap-3">
        <img
          src="/icon-192x192.png"
          alt=""
          className="h-8 w-8 shrink-0 rounded"
        />
        <p className="min-w-0 flex-1 text-sm leading-tight font-semibold">
          <Trans>Install Fiduly Expenses</Trans>
        </p>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="text-primary shrink-0 text-xs font-medium"
        >
          {expanded ? <Trans>Hide</Trans> : <Trans>How?</Trans>}
        </button>
      </div>
      {expanded && (
        <ol className="text-muted-foreground mt-2 space-y-1 pl-4 text-xs leading-relaxed">
          <li className="flex items-center gap-1.5">
            <span className="text-foreground font-medium">1.</span>
            <Trans>
              Tap{' '}
              <MoreIcon className="text-primary -mt-px inline-block align-middle" />{' '}
              in the toolbar
            </Trans>
          </li>
          <li className="flex items-center gap-1.5">
            <span className="text-foreground font-medium">2.</span>
            <Trans>
              Tap{' '}
              <ShareIcon className="text-primary -mt-px inline-block align-middle" />{' '}
              Share
            </Trans>
          </li>
          <li className="flex items-center gap-1.5">
            <span className="text-foreground font-medium">3.</span>
            <Trans>
              Scroll down, tap <strong>"Add to Home Screen"</strong>
            </Trans>
          </li>
        </ol>
      )}
    </Banner>
  )
}
